import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { uploadsApi } from '@/api/uploads';
import {
    FooterCustomizations,
    HeaderCustomizations,
    ReportData,
    TableCustomizations,
    DividerCustomizations,
} from '@/components/preview/types';
import { DEFAULT_FONT } from '@/constants';
import { useAuth } from '@/context/AuthContext';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { extractApiErrorMessage } from '@/lib/api-errors';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { INITIAL_FORM_DATA } from '@/pages/create-report/constants';
import { setPreviewUrl } from '@/utils/preview-cache';
import { validateImageFile } from '@/utils/validate-image-file';
import type { ActiveSection, ColorTarget, FileUploadState, PresetColors } from '../types';

interface UseCustomizeStateOptions {
    data: ReportData;
    onUpdate: (updates: Partial<ReportData> | ((prev: ReportData) => Partial<ReportData>)) => void;
    onOpenChange: (open: boolean) => void;
    open: boolean;
    /** Section to expand on open; null keeps whatever the user last opened. */
    initialSection?: ActiveSection;
}

export function useCustomizeState({
    data,
    onUpdate,
    onOpenChange,
    open,
    initialSection = null,
}: UseCustomizeStateOptions) {
    const { user } = useAuth();
    const hasCrp = !!user?.crp;
    const hasPhone = !!user?.phone;
    const hasInstagram = !!user?.instagram;
    const letterheadAccess = useFeatureEntitlement('reports.letterhead');
    const customizations = data.customizations ?? INITIAL_FORM_DATA.customizations!;

    const fontConfig = customizations?.font || { family: DEFAULT_FONT };
    const colors = customizations.colors ?? INITIAL_FORM_DATA.customizations?.colors;

    // --- Local customization state ---
    // One place for the seed values so opening the modal and re-seeding it after a
    // dismissal can never drift apart.
    const seed = {
        primary: colors?.primary,
        secondary: colors?.secondary,
        font: fontConfig.family,
        header: customizations?.header || ({ alignment: 'left' } as HeaderCustomizations),
        table: customizations?.table || {
            text_alignment: 'center' as const,
            header_color: undefined,
        },
        footer: customizations?.footer || {
            padding: 12,
            email_visible: true,
            instagram_visible: true,
            phone_number_visible: true,
            psychologist_name_visible: true,
        },
        divider: customizations?.divider || {
            alignment: 'center' as const,
            background_color: 'primary',
            show_line: true,
            font_style: 'normal' as const,
        },
    };
    const seedRef = useRef(seed);
    seedRef.current = seed;

    const [localPrimary, setLocalPrimary] = useState(seed.primary);
    const [localSecondary, setLocalSecondary] = useState(seed.secondary);
    const [localFont, setLocalFont] = useState(seed.font);

    const [localHeaderConfig, setLocalHeaderConfig] = useState<HeaderCustomizations>(seed.header);
    const [localTableConfig, setLocalTableConfig] = useState<TableCustomizations>(seed.table);
    const [localFooterConfig, setLocalFooterConfig] = useState<FooterCustomizations>(seed.footer);
    const [localDividerConfig, setLocalDividerConfig] = useState<DividerCustomizations>(
        seed.divider,
    );

    // --- Accordion-style active section ---
    const [activeSection, setActiveSection] = useState<ActiveSection>(null);

    // Which row of "Onde estas cores aparecem" is expanded. It also steers the
    // preview, so the element being recoloured is the one on the page.
    const [colorTarget, setColorTarget] = useState<ColorTarget>(null);

    // The modal stays mounted, so a deep link has to re-assert its section on
    // every open. Without one the accordion is left where the user put it.
    useEffect(() => {
        if (open && initialSection) setActiveSection(initialSection);
    }, [open, initialSection]);

    // --- File upload state ---
    const logoInputRef = useRef<HTMLInputElement>(null);
    const letterheadInputRef = useRef<HTMLInputElement>(null);

    const [logoUploadState, setLogoUploadState] = useState<FileUploadState>({
        status: 'idle',
        progress: 0,
    });
    const [letterheadUploadState, setLetterheadUploadState] = useState<FileUploadState>({
        status: 'idle',
        progress: 0,
    });

    // --- Re-seed on every open ---
    // The modal never unmounts, so without this a dismissed edit stays in local
    // state and the panel goes on showing a laudo the document does not have.
    // Re-seeding on open (rather than on close) covers every exit — Cancelar, ESC,
    // the X and the backdrop all land here next time — and keeps `handleApply`
    // clear of the parent's not-yet-flushed state. Assignments are unconditional:
    // a discarded value has to be able to go back to `undefined`.
    const wasOpen = useRef(open);
    useEffect(() => {
        if (open && !wasOpen.current) {
            const next = seedRef.current;
            setLocalPrimary(next.primary);
            setLocalSecondary(next.secondary);
            setLocalFont(next.font);
            setLocalHeaderConfig(next.header);
            setLocalTableConfig(next.table);
            setLocalFooterConfig(next.footer);
            setLocalDividerConfig(next.divider);

            // A discarded upload must not leave its "Arquivo carregado" behind.
            setLogoUploadState({ status: 'idle', progress: 0 });
            setLetterheadUploadState({ status: 'idle', progress: 0 });
            if (logoInputRef.current) logoInputRef.current.value = '';
            if (letterheadInputRef.current) letterheadInputRef.current.value = '';

            setColorTarget(null);
        }
        wasOpen.current = open;
    }, [open]);

    // --- Config updaters ---
    const updateHeaderConfig = useCallback((updates: Partial<HeaderCustomizations>) => {
        setLocalHeaderConfig((prev) => ({ ...prev, ...updates }));
    }, []);

    const updateTableConfig = useCallback((updates: Partial<TableCustomizations>) => {
        setLocalTableConfig((prev) => ({ ...prev, ...updates }));
    }, []);

    const updateFooterConfig = useCallback((updates: Partial<FooterCustomizations>) => {
        setLocalFooterConfig((prev) => ({ ...prev, ...updates }));
    }, []);

    const updateDividerConfig = useCallback((updates: Partial<DividerCustomizations>) => {
        setLocalDividerConfig((prev) => ({ ...prev, ...updates }));
    }, []);

    // --- File upload logic ---
    const handleS3Upload = async (file: File, type: 'logo' | 'letterhead') => {
        if (type === 'letterhead' && letterheadAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(letterheadAccess.decision));
            setLetterheadUploadState({ status: 'idle', progress: 0 });
            return;
        }

        const setUploadState = type === 'logo' ? setLogoUploadState : setLetterheadUploadState;

        const error = validateImageFile(file);
        if (error) {
            setUploadState({ status: 'error', progress: 0, error });
            return;
        }

        setUploadState({ status: 'uploading', progress: 0 });

        try {
            // The confirmed reference — not the staging key — is what gets
            // stored and what the preview is later resolved by.
            const assetRef = await uploadsApi.uploadAndConfirm({
                file,
                intent: type,
                onProgress: (progress) => setUploadState((prev) => ({ ...prev, progress })),
            });

            if (type === 'logo') {
                updateHeaderConfig({ logo_url: assetRef });
            } else {
                updateHeaderConfig({ letterhead_url: assetRef });
            }
            setPreviewUrl(assetRef, URL.createObjectURL(file));

            setUploadState({ status: 'success', progress: 100 });
        } catch (err) {
            console.error('Upload failed', err);
            setUploadState({
                status: 'error',
                progress: 0,
                // The backend can genuinely reject an image now (corrupt,
                // oversized), and its pt-BR message says which.
                error: extractApiErrorMessage(err, 'Falha no upload. Tente novamente.'),
            });
        }
    };

    const handleFileUpload = (
        e: React.ChangeEvent<HTMLInputElement>,
        type: 'logo' | 'letterhead',
    ) => {
        const file = e.target.files?.[0];
        if (file) {
            handleS3Upload(file, type);
        }
    };

    const handleDeleteImage = (type: 'logo' | 'letterhead') => {
        if (type === 'logo') {
            updateHeaderConfig({ logo_url: undefined });
            setLogoUploadState({ status: 'idle', progress: 0 });
            if (logoInputRef.current) logoInputRef.current.value = '';
        } else {
            updateHeaderConfig({ letterhead_url: undefined });
            setLetterheadUploadState({ status: 'idle', progress: 0 });
            if (letterheadInputRef.current) letterheadInputRef.current.value = '';
        }
    };

    // --- Apply / Cancel ---
    const handleApply = () => {
        onUpdate((prevData) => ({
            customizations: {
                ...prevData.customizations,
                header: { ...localHeaderConfig },
                table: localTableConfig,
                divider: localDividerConfig,
                colors: {
                    primary: localPrimary,
                    secondary: localSecondary,
                },
                font: { family: localFont },
                footer: { ...localFooterConfig },
            },
        }));
        toast.success('Alterações salvas com sucesso');
        onOpenChange(false);
    };

    const handleCancel = () => {
        onOpenChange(false);
    };

    // --- Preset click ---
    const handlePresetClick = (preset: PresetColors) => {
        setLocalPrimary(preset.primary);
        setLocalSecondary(preset.secondary);
    };

    // --- Section toggle (accordion) ---
    const toggleSection = useCallback((section: ActiveSection) => {
        setActiveSection((prev) => (prev === section ? null : section));
        // Leaving Cores hands the preview back to whatever the next section shows.
        if (section !== 'colors') setColorTarget(null);
    }, []);

    return {
        // User info flags
        hasCrp,
        hasPhone,
        hasInstagram,

        // Local customization state
        localPrimary,
        setLocalPrimary,
        localSecondary,
        setLocalSecondary,
        localFont,
        setLocalFont,
        localHeaderConfig,
        localTableConfig,
        localFooterConfig,
        localDividerConfig,

        // Updaters
        updateHeaderConfig,
        updateTableConfig,
        updateFooterConfig,
        updateDividerConfig,

        // Active section (accordion)
        activeSection,
        toggleSection,
        colorTarget,
        setColorTarget,

        // File upload
        logoInputRef,
        letterheadInputRef,
        logoUploadState,
        letterheadUploadState,
        handleFileUpload,
        handleDeleteImage,

        // User info for preview
        psychologistName: user?.full_name || '',
        psychologistCrp: user?.crp,

        // Actions
        handleApply,
        handleCancel,
        handlePresetClick,
    };
}
